import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { match } from 'ts-pattern'
import { IconButtonSize, IconButtonVariation } from 'components/IconButton'
import { unifiedColors } from 'styles/colors'
import { theme, themeUtils } from 'styles/theme'

interface StyledIconButtonProps {
  $size: IconButtonSize
  $variation?: IconButtonVariation
  $isDisabled?: boolean
}

const getButtonWrapperBackground = (variation?: IconButtonVariation) => {
  switch (variation) {
    case IconButtonVariation.greyFull:
      return theme.palette.surface.tertiaryDark
    case IconButtonVariation.primary:
      return unifiedColors.button.primary.default
    case IconButtonVariation.white:
      return theme.palette.surface.default
    default:
      return 'none'
  }
}

const getButtonWrapperBorder = (variation?: IconButtonVariation) =>
  match(variation)
    .with(
      IconButtonVariation.whiteOutline,
      () => `solid ${theme.palette.surface.default} 2px`
    )
    .with(
      IconButtonVariation.white,
      () => `solid ${theme.palette.surface.secondary} 1px`
    )
    .with(
      IconButtonVariation.blackOutline,
      () => `solid ${theme.palette.label.default} 2px`
    )
    .otherwise(() => 'none')

const getButtonWrapperCSS = ({
  $size,
  $variation,
  $isDisabled
}: StyledIconButtonProps) => css`
  background-color: ${getButtonWrapperBackground($variation)};
  border: ${getButtonWrapperBorder($variation)};
  border-radius: ${$size === IconButtonSize.regular ? '12px' : '12px'};
  width: ${$size === IconButtonSize.regular ? '48px' : '44px'};
  height: ${$size === IconButtonSize.regular ? '48px' : '44px'};
  ${themeUtils.flexCenter}
  cursor: ${$isDisabled ? 'auto' : 'pointer'};
`

export const StyledButtonWrapper = styled.button<StyledIconButtonProps>`
  ${({ $size, $variation, $isDisabled }) =>
    getButtonWrapperCSS({ $size, $variation, $isDisabled })}
`

export const StyledDivWrapper = styled.div<StyledIconButtonProps>`
  ${({ $size, $variation, $isDisabled }) =>
    getButtonWrapperCSS({ $size, $variation, $isDisabled })}
  position: relative;
`

const getIconColor = (
  variation?: IconButtonVariation,
  isDisabled?: boolean
) => {
  if (isDisabled) return '#A6A692'
  switch (variation) {
    case IconButtonVariation.whiteOutline:
      return theme.palette.label.default
    case IconButtonVariation.blackOutline:
      return theme.palette.label.default
    case IconButtonVariation.white:
      return theme.palette.label.default
    default:
      return `white`
  }
}

export const getIconFactoryCSS = ({
  $size,
  $variation,
  $isDisabled
}: StyledIconButtonProps) => css`
  width: ${$size === IconButtonSize.regular ? '20px' : '18px'};
  height: ${$size === IconButtonSize.regular ? '20px' : '18px'};
  & path {
    fill: ${getIconColor($variation, $isDisabled)};
  }
`

export const YellowDot = styled.div`
  width: 19px;
  height: 19px;
  position: absolute;
  right: -8px;
  top: -5px;
  border-radius: 12px;
  background: ${unifiedColors.surface.pink};
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${theme.palette.label.default};
  ${css({ ...theme.typography.bodyMBold })};
`
